import {
  ArrayInput,
  AutocompleteInput,
  ButtonProps,
  Create,
  DeleteWithConfirmButton,
  Edit,
  FormDataConsumer,
  InputHelperText,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  maxLength,
  minLength,
  minValue,
  required,
  useDataProvider,
  useGetIdentity,
  useInput,
  useNotify,
  useRecordContext,
  useRedirect,
  useSaveContext,
  useSimpleFormIterator,
  useUpdate,
} from "react-admin";
import {
  Divider,
  Typography,
  Grid,
  Button as MuiButton,
  Paper,
  CircularProgress,
  Autocomplete,
  TextField as MuiTextInput,
} from "@mui/material";
import styled, { createGlobalStyle } from "styled-components";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  DeliveryNote,
  OrderStatus,
  OrderTripType,
  type Order,
} from "../../@generated/schemas";
import dayjs from "dayjs";
import gql from "graphql-tag";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { DownloadDeliveryNoteZip } from "./list";
import AddIcon from "@mui/icons-material/Add";
import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  createRef,
} from "react";
import { debounce, uniqBy } from "lodash";
import { SaveButton } from "src/common/SaveButton";
import {
  useFetchActionsQuery,
  useFetchProfitCentersQuery,
} from "src/@generated/hooks";
import DateInput from "src/common/DateInput";
import { useTranslate } from "src/locales";
import businessDays from "dayjs-business-days";

dayjs.extend(businessDays);

const CONFIRM_ORDER_MUTATION = gql`
  mutation ConfirmOrder($id: String!) {
    confirmOrder(id: $id)
  }
`;

const TownTextField = forwardRef(({ ...props }: any, ref) => {
  const translate = useTranslate();
  const { onChange, onBlur, label, helperText, ...rest } = props;
  const dataProvider = useDataProvider();
  const record = useRecordContext();
  const { setValue, getValues } = useFormContext();
  const [searchInput, setSearchInput] = useState("");
  const { field } = useInput({
    onChange,
    onBlur,
    ...rest,
    defaultValue: props.defaultValue,
  });

  useEffect(() => {
    async function init() {
      if (!props.defaultValue && getValues(props.addressSource)) {
        const location = await dataProvider.getList("Location", {
          filter: {
            id: {
              equals: getValues(props.addressSource),
            },
            integrationType: {
              equals: record?.integrationType,
            },
          },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
        });
        if (location?.data?.[0]?.town) {
          setValue(props.source, location.data[0].town);
        }
      }
    }
    init();
  }, []);

  const [cities, setCities] = useState([]);

  const handleLoadCities = async (_: any, value: string) => {
    const response = await dataProvider.getList("Location", {
      filter: {
        town: value || "",
        integrationType: {
          equals: record?.integrationType,
        },
      },
      pagination: {
        page: 1,
        perPage: 1000,
      },
      sort: {
        field: "town",
        order: "DESC",
      },
    });

    const data = uniqBy(response.data, "town");

    setCities(data);

    return data;
  };

  const handleSelectTown = (event: any, value: string) => {
    setSearchInput(value || "");
    setValue(props.source, value);
  };

  const searchDelayed = useMemo(
    () => debounce(handleLoadCities, 300),
    [handleLoadCities],
  );

  const handleOnInputChange = (value: string) => {
    setSearchInput(value || "");
    searchDelayed(undefined, value);
  };

  useImperativeHandle(
    ref,
    () => ({
      setValue: (value: string) => {
        // inputSetValue(value);
        setSearchInput(value || "");
      },
      getValue: () => {
        return searchInput;
      },
    }),
    [],
  );

  return (
    <Autocomplete
      {...field}
      autoComplete={true}
      // label={label}
      style={{ marginBottom: 26 }}
      freeSolo
      inputValue={searchInput}
      disabled={props.readOnly}
      defaultValue={props.defaultCity}
      onChange={handleSelectTown}
      onInputChange={(event: any) =>
        handleOnInputChange(event?.target?.value || "")
      }
      onFocus={handleLoadCities as any}
      options={cities.map((option) => option.town)}
      renderInput={(params) => (
        <MuiTextInput
          {...params}
          disabled={props.readOnly}
          variant="filled"
          size="small"
          label={translate("resources.Order.fields.city")}
        />
      )}
      {...rest}
    />
  );
});

const AddressTextField = forwardRef(({ ...props }: any, ref) => {
  const { onChange, onBlur, label, helperText, defaultLocation, ...rest } =
    props;
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const { setValue, getValues, watch } = useFormContext();
  const record = useRecordContext();
  const [addresses, setAddresses] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const watchLocationCity = watch(props.citySource || null);
  const watchProfitCenter = watch("profitCenter");

  const getOptions = (options) => {
    return options.map((row) => ({
      label: `${row.name}, ${row.street}, ${row.town} (${row.customerNumber})`,
      city: row.town,
      id: row.id,
    }));
  };
  const options = getOptions(addresses);

  const {
    field,
    fieldState: { invalid, error, isTouched },
    isRequired,
  } = useInput({
    onChange,
    onBlur,
    ...rest,
    defaultValue: record?.[props.source] || props.defaultLocation || "",
  });

  const defaultProfitCenter =
    identity?.userDefaultValues?.find(
      (item) => item.property === "PROFIT_CENTER",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "PROFIT_CENTER",
    );

  const handleLoadAddress = async (_: any, value?: string, id?: string) => {
    const response = await dataProvider.getList("Location", {
      filter:
        id && id !== "input"
          ? {
              id,
              integrationType: {
                equals: record?.integrationType,
              },
              profitCenter: watchProfitCenter || defaultProfitCenter?.value,
              [watchLocationCity || props.defaultCity ? "town" : ""]:
                watchLocationCity || props.defaultCity || undefined,
            }
          : {
              q: value,
              integrationType: {
                equals: record?.integrationType,
              },
              profitCenter: watchProfitCenter || defaultProfitCenter?.value,
              [watchLocationCity || props.defaultCity ? "town" : ""]:
                watchLocationCity || props.defaultCity || undefined,
            },
      pagination: {
        page: 1,
        perPage: 30,
      },
      sort: {
        field: "name",
        order: "DESC",
      },
    });

    setAddresses(response.data);

    return response.data;
  };

  useEffect(() => {
    async function init() {
      const result = await handleLoadAddress(
        null,
        record?.[props.source] ||
          props.defaultLocation ||
          getValues(props.source),
        record?.[props.source] ||
          props.defaultLocation ||
          getValues(props.source),
      );

      if (record?.[props.source] || props.defaultLocation) {
        const foundOption = getOptions(result)?.find(
          (item) => item.id === record?.[props.source] || props.defaultLocation,
        );
        setSearchInput(foundOption?.label || "");

        if (props.cityRef.current) {
          props.cityRef.current.setValue(foundOption?.city || "");
        }
      }
    }
    init();
  }, [
    props.source,
    props.defaultLocation,
    watchProfitCenter,
    defaultProfitCenter,
    record?.[props.source],
  ]);

  useEffect(() => {
    async function init() {
      await handleLoadAddress(null, watchLocationCity);
    }
    watchLocationCity && init();
  }, [watchLocationCity, watchProfitCenter, defaultProfitCenter]);

  const searchDelayed = useMemo(
    () => debounce(handleLoadAddress, 300),
    [handleLoadAddress],
  );

  const handleSearchInput = (event: any, value: any) => {
    value && setSearchInput(value);
    return searchDelayed(event, value);
  };

  const handleSelectAddress = (
    event: any,
    value: string,
    reason: any,
    detail: any,
  ) => {
    setSearchInput(detail?.option?.label || "");
    setValue(props.source, detail?.option?.id || null);

    if (detail?.option?.id && props.cityRef.current) {
      props.cityRef.current.setValue(detail?.option?.city);
    }

    if (props.source.includes("orderTrips")) {
      const deliveryNotes = getValues("deliveryNotes");
      deliveryNotes.map((_, key) => {
        setValue(`deliveryNotes.${key}.locationId`, detail?.option?.id);
      });
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      setValue: (value: string) => {
        //
      },
    }),
    [],
  );

  return (
    <Autocomplete
      {...field}
      placeholder={(label || "") as string}
      inputValue={searchInput}
      disabled={props.readOnly}
      style={{ marginBottom: 26 }}
      freeSolo
      getOptionLabel={(option: any) => option?.label || ""}
      // getOptionKey={(option: any) => option.id}
      onChange={handleSelectAddress}
      openOnFocus
      // onFocus={handleLoadAddress}
      onInputChange={handleSearchInput}
      options={options}
      renderInput={(params) => {
        // console.log('params', params);
        return (
          <MuiTextInput
            {...params}
            variant="filled"
            size="small"
            // defaultValue={defaultOption?.label}
            disabled={props.readOnly}
            label={label}
            error={invalid}
            required={isRequired}
            helperText={
              invalid ? (
                <InputHelperText
                  error={error?.message}
                  helperText={helperText}
                  touched={isTouched}
                />
              ) : (
                ""
              )
            }
          />
        );
      }}
      {...rest}
    />
  );
});

const MultipleUnloading = () => {
  const translate = useTranslate();
  const source = "orderTrips";
  const { control, watch, setValue } = useFormContext();
  const [fakeSetup, setFakeSetup] = useState(false);
  const data = watch() as Order;
  const unloadingCityRefs = useRef<any[]>([]);
  const unloadingLocationIdRefs = useRef<any[]>([]);
  const { fields, append, remove } = useFieldArray({ control, name: source });
  const identity = JSON.parse(localStorage.getItem("user"));
  const defaultUnloadingDate =
    identity?.userDefaultValues?.find(
      (item) => item.property === "UNLOADING_DATE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "UNLOADING_DATE",
    );
  const defaultUnloadingCity =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_CITY",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_CITY",
    );
  const defaultUnloadingLocation =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_LOCATION_ID",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_LOCATION_ID",
    );

  const isDisabledForm =
    ![OrderStatus.InProgress, undefined].includes(data?.status) ||
    (!!data?.id &&
      !data?.externalSystemOrderNumberCarlo &&
      !data?.externalSystemOrderNumberIngres) ||
    identity?.selectedCompany?.isBlocked;

  useEffect(() => {
    if (data.orderTrips.length === 0 && data.unloadingLocationId && data.id) {
      setFakeSetup(true);
      setValue("orderTrips", [
        {
          id: "FAKE",
          locationId: data.unloadingLocationId,
          date: data.unloadingDate,
          note: data.unloadingNote,
          tripType: OrderTripType.Unloading,
          orderId: data.id,
          orderNumber: 1,
        },
      ]);
    }
  }, [data]);

  return (
    <div>
      {fields.map((field: any, index) => {
        if (!unloadingCityRefs.current[index]) {
          unloadingCityRefs.current[index] = createRef();
        }
        if (!unloadingLocationIdRefs.current[index]) {
          unloadingLocationIdRefs.current[index] = createRef();
        }

        return (
          <div key={field.id}>
            <h4>{translate("resources.Order.fields.unloading")}:</h4>

            <DateInput
              source={`${source}[${index}].date`}
              label={translate("resources.Order.fields.date")}
              size="small"
              fullWidth
              // defaultValue={defaultUnloadingDate?.value}
              readOnly={isDisabledForm}
              validate={[
                required(),
                (value, values) => {
                  if (
                    value &&
                    values.loadingDate &&
                    dayjs(value).isBefore(dayjs(values.loadingDate), "day")
                  ) {
                    return translate("resources.Order.errors.unloadingAge");
                  }

                  return undefined;
                },
              ]}
            />
            <div style={{ width: "100%" }}>
              <TownTextField
                source={`${source}[${index}].city`}
                defaultValue={(defaultUnloadingCity?.value || "").toUpperCase()}
                addressSource={`${source}[${index}].locationId`}
                readOnly={isDisabledForm}
                ref={unloadingCityRefs.current[index]}
                addressRef={unloadingLocationIdRefs.current[index]}
                label={translate("resources.Order.fields.city")}
              />
            </div>
            <div style={{ width: "100%" }}>
              <AddressTextField
                source={`${source}[${index}].locationId`}
                citySource={`${source}[${index}].city`}
                ref={unloadingLocationIdRefs.current[index]}
                cityRef={unloadingCityRefs.current[index]}
                defaultLocation={
                  field.locationId || defaultUnloadingLocation?.value
                }
                defaultCity={(defaultUnloadingCity?.value || "").toUpperCase()}
                required
                validate={required()}
                readOnly={isDisabledForm}
                label={translate("resources.Order.fields.street")}
              />
            </div>
            <TextInput
              source={`${source}[${index}].note`}
              size="small"
              readOnly={isDisabledForm}
              label={translate("resources.Order.fields.unloadingNote")}
              fullWidth
            />

            {fields.length > 1 && (
              <MuiButton color="error" onClick={() => remove(index)}>
                {translate("resources.Global.delete")}
              </MuiButton>
            )}
          </div>
        );
      })}
      <MuiButton
        disabled={
          (data?.orderTrips?.[0]?.id === "FAKE" && data.id) || isDisabledForm
            ? true
            : false
        }
        onClick={() =>
          append({
            date: defaultUnloadingDate?.value
              ? dayjs()
                  .businessDaysAdd(defaultUnloadingDate?.value)
                  .startOf("day")
                  .toDate()
              : dayjs().startOf("day").toDate(),
            city: (defaultUnloadingCity?.value || "").toUpperCase(),
            locationId: defaultUnloadingLocation?.value,
            tripType: OrderTripType.Unloading,
            orderNumber: fields.length,
            note: "",
          })
        }
      >
        {translate("resources.Global.add")}
      </MuiButton>
    </div>
  );
};

const AddDeliveryNoteButton = (props: ButtonProps) => {
  const { add } = useSimpleFormIterator();
  const translate = useTranslate();

  return (
    <MuiButton variant="contained" onClick={() => add()} {...props}>
      {translate("resources.Order.fields.addDeliveryNote")}
    </MuiButton>
  );
};

const Form = (props: any) => {
  const translate = useTranslate();
  const record = useRecordContext<Order>();
  const { watch, setValue, getFieldState } = useFormContext();
  const data = watch() as Order;
  const identity = JSON.parse(localStorage.getItem("user"));
  const loadingLocationIdRef = useRef<any>(null);
  const loadingCityRef = useRef<any>(null);

  const isDisabledDeliveryNotes =
    (!!data?.id &&
      !data?.externalSystemOrderNumberCarlo &&
      !data?.externalSystemOrderNumberIngres) ||
    identity?.selectedCompany?.isBlocked;
  const isDisabledForm =
    ![OrderStatus.InProgress, undefined].includes(data?.status) ||
    isDisabledDeliveryNotes ||
    identity?.selectedCompany?.isBlocked;

  const defaultCurrency =
    identity?.userDefaultValues?.find((item) => item.property === "CURRENCY") ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "CURRENCY",
    );

  const defaultLoadingDate =
    identity?.userDefaultValues?.find(
      (item) => item.property === "LOADING_DATE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "LOADING_DATE",
    );
  const defaultLoadingLocation =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_LOCATION_ID",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_LOCATION_ID",
    );
  const defaultLoadingCity =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_CITY",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_CITY",
    );
  const defaultUnloadingDate =
    identity?.userDefaultValues?.find(
      (item) => item.property === "UNLOADING_DATE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "UNLOADING_DATE",
    );
  const defaultOrderType =
    identity?.userDefaultValues?.find(
      (item) => item.property === "ORDER_TYPE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ORDER_TYPE",
    );
  // const defaultEnabledDeliveryNoteHalfPallets =
  //   identity?.selectedCompany?.companyDefaultValues?.find(
  //     (item) => item.property === "ENABLED_DELIVERY_NOTE_HALF_PALLETS"
  //   );
  const defaultEnabledDeliveryNoteLoadingUnitsValidation =
    identity?.userDefaultValues?.find(
      (item) =>
        item.property === "ENABLED_DELIVERY_NOTE_LOADING_UNITS_VALIDATION",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) =>
        item.property === "ENABLED_DELIVERY_NOTE_LOADING_UNITS_VALIDATION",
    );
  // const defaultEnabledDeliveryNotePackages =
  //   identity?.selectedCompany?.companyDefaultValues?.find(
  //     (item) => item.property === "ENABLED_DELIVERY_NOTE_PACKAGES"
  //   );
  const defaultProfitCenter =
    identity?.userDefaultValues?.find(
      (item) => item.property === "PROFIT_CENTER",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "PROFIT_CENTER",
    );
  const defaultAction =
    identity?.userDefaultValues?.find((item) => item.property === "ACTION") ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ACTION",
    );
  const defaultEnabledMultipleProfitCenters =
    identity?.userDefaultValues?.find(
      (item) => item.property === "ENABLED_MULTIPLE_PROFIT_CENTERS",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ENABLED_MULTIPLE_PROFIT_CENTERS",
    );
  const defaultEnabledMultipleActions =
    identity?.userDefaultValues?.find(
      (item) => item.property === "ENABLED_MULTIPLE_ACTIONS",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ENABLED_MULTIPLE_ACTIONS",
    );
  const AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE =
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE",
    );
  const AUTO_COPY_PALLET_PLACES_TO_SSCC =
    identity?.userDefaultValues?.find(
      (item) => item.property === "AUTO_COPY_PALLET_PLACES_TO_SSCC",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "AUTO_COPY_PALLET_PLACES_TO_SSCC",
    );
  // const ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES =
  //   identity?.selectedCompany?.companyDefaultValues?.find(
  //     (item) => item.property === "ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES"
  //   );
  const AUTO_COPY_SSCC_TO_LOADING_UNITS =
    identity?.userDefaultValues?.find(
      (item) => item.property === "AUTO_COPY_SSCC_TO_LOADING_UNITS",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "AUTO_COPY_SSCC_TO_LOADING_UNITS",
    );
  const DEFAULT_LOADING_UNIT =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_UNIT",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_UNIT",
    );
  const ENABLED_SALES_TYPES =
    identity?.userDefaultValues?.find(
      (item) => item.property === "ENABLED_SALES_TYPES",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ENABLED_SALES_TYPES",
    );

  const { data: profitCenters, loading: loadingProfitCenters } =
    useFetchProfitCentersQuery({
      skip: defaultEnabledMultipleProfitCenters?.value !== "TRUE",
    });
  const { data: actions, loading: loadingActions } = useFetchActionsQuery({
    skip: defaultEnabledMultipleActions?.value !== "TRUE",
  });

  const handleOnChangeDeliveryNotes = () => {
    data.deliveryNotes.forEach((deliveryNote, key) => {
      let numberOfItems = 0;
      deliveryNote.loadingUnits.forEach((loadingUnit) => {
        numberOfItems += loadingUnit.amount;
      });
      setValue(`deliveryNotes.${key}.numberOfItems`, numberOfItems);
    });
  };

  useEffect(() => {
    if (!getFieldState("noteForInvoice")?.isTouched) {
      setValue("noteForInvoice", data.referenceOrderNumber);
    }
    if (!getFieldState("noteOnInvoice")?.isTouched) {
      setValue("noteOnInvoice", data.referenceOrderNumber);
    }

    if (AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE?.value === "TRUE") {
      setValue(`deliveryNotes.0.deliveryNoteNumber`, data.referenceOrderNumber);
    }
  }, [data.referenceOrderNumber]);

  useEffect(() => {
    if (AUTO_COPY_PALLET_PLACES_TO_SSCC?.value === "TRUE") {
      setValue(`deliveryNotes.0.numberOfRequiredSscc`, data.numberOfItems);
    }
  }, [data.numberOfItems]);

  useEffect(() => {
    // cloning
    if (window.location.hash.includes("create?source=") && record) {
      if (defaultLoadingDate) {
        setValue(
          "loadingDate",
          defaultLoadingDate?.value
            ? dayjs()
                .businessDaysAdd(defaultLoadingDate?.value)
                .startOf("day")
                .toDate()
            : dayjs().startOf("day").toDate(),
        );
      }
      if (defaultUnloadingDate) {
        setValue(
          "unloadingDate",
          defaultUnloadingDate?.value
            ? dayjs()
                .businessDaysAdd(defaultUnloadingDate?.value)
                .startOf("day")
                .toDate()
            : dayjs().startOf("day").toDate(),
        );
      }

      setValue("referenceOrderNumber", "");
      setValue("orderNumber", "");
      setValue("loadingNote", "");
      setValue("unloadingNote", "");
      setValue("salesType", undefined);
      setValue("status", undefined);
      setValue("user_id", undefined);
      setValue("userId", undefined);
      setValue("user", undefined);
      setValue("company", undefined);
      setValue("deliveryNotes_ids", undefined);
      setValue("orderTrips_ids", undefined);
      setValue("caveats_ids", undefined);
      setValue("caveats", undefined);
      setValue("confimedAt", undefined);
      setValue("orderCost", undefined);
      setValue("orderCurrency", undefined);
      setValue("externalSystemOrderNumberCarlo", undefined);
      setValue("externalSystemOrderNumberIngres", undefined);
      record.deliveryNotes.map((deliveryNote, key) => {
        setValue(`deliveryNotes.${key}.deliveryNoteNumber`, "");
        setValue(`deliveryNotes.${key}.id`, undefined);
        setValue(`deliveryNotes.${key}.sscc`, undefined);
      });
      record.orderTrips.map((deliveryNote, key) => {
        setValue(`orderTrips.${key}.id`, undefined);
      });
    }
  }, [record]);

  const validateDeliveryNotes = () => {
    const { getValues } = useFormContext();

    return (value: string, allValues: any, meta: any) => {
      const deliveryNotes = getValues("deliveryNotes") || [];

      if (
        deliveryNotes?.length > 0 &&
        uniqBy(deliveryNotes, "deliveryNoteNumber").length !==
          deliveryNotes.length
      ) {
        return "Číslo musí být unikátní";
      }

      return undefined;
    };
  };

  return (
    <Grid container spacing={2}>
      <ScrollableElement />
      <Grid item xs={6} direction="column">
        <Typography variant="h6">
          {record?.id
            ? `${translate("resources.Order.fields.editOrder")} ${
                record.orderNumber || "-"
              } (${record.referenceOrderNumber || "-"})`
            : translate("resources.Order.fields.createOrder")}
        </Typography>
        <Typography variant="h6">
          {translate("resources.Order.fields.status")}:{" "}
          {record?.id && record.hasOwnProperty("status")
            ? `${(record as Order)?.status}`
            : translate("resources.Order.fields.creating")}
        </Typography>

        <Divider />
        <br />

        <Grid container spacing={2}>
          <Grid item xs={6} direction="column">
            <TextInput
              size="small"
              source="referenceOrderNumber"
              required
              readOnly={!!record?.id || identity?.selectedCompany?.isBlocked}
              validate={[minLength(1), maxLength(32)]}
              fullWidth
            />

            {ENABLED_SALES_TYPES?.value === "TRUE" ? (
              <SelectInput
                size="small"
                source="salesType"
                optionText="name"
                readOnly={isDisabledForm}
                choices={[
                  {
                    id: "LAPREVOZ",
                    name: "0 - Převoz",
                  },
                  {
                    id: "LAVRATKY",
                    name: "1 - Vratka",
                  },
                  {
                    id: "LASVOZ",
                    name: "2 - Svoz palet",
                  },
                  {
                    id: "LAZAVOZ",
                    name: "3 - Přímý závoz",
                  },
                  {
                    id: "LAKUN",
                    name: "4 - Rozvoz kun",
                  },
                ]}
                validate={[required()]}
                fullWidth
              />
            ) : null}

            {defaultEnabledMultipleProfitCenters?.value === "TRUE" ? (
              <SelectInput
                size="small"
                source="profitCenter"
                isLoading={loadingProfitCenters}
                defaultValue={defaultProfitCenter?.value}
                optionText="name"
                readOnly={isDisabledForm}
                choices={
                  profitCenters?.fetchProfitCenters?.map((item) => ({
                    id: item.code,
                    name: `${item.code} (${item.name})`,
                  })) || []
                }
                validate={[required()]}
                fullWidth
              />
            ) : null}

            <Paper style={{ padding: 12, paddingTop: 6, paddingBottom: 6 }}>
              <h4>{translate("resources.Order.fields.loading")}:</h4>
              <DateInput
                size="small"
                source="loadingDate"
                label={translate("resources.Order.fields.date")}
                // defaultValue={defaultLoadingDate?.value}
                readOnly={isDisabledForm}
                fullWidth
                validate={[
                  required(),
                  (value, values) => {
                    const hasInvalidUnloading = values.orderTrips?.some(
                      (trip) =>
                        trip.date &&
                        dayjs(trip.date).isBefore(dayjs(value), "day"),
                    );

                    if (hasInvalidUnloading) {
                      return translate("resources.Order.errors.loadingAge");
                    }

                    return undefined;
                  },
                ]}
              />
              <TownTextField
                source="loadingCity"
                addressSource="loadingLocationId"
                defaultValue={(defaultLoadingCity?.value || "").toUpperCase()}
                readOnly={isDisabledForm}
                ref={loadingCityRef}
                addressRef={loadingLocationIdRef}
              />
              <AddressTextField
                source="loadingLocationId"
                required
                ref={loadingLocationIdRef}
                citySource="loadingCity"
                cityRef={loadingCityRef}
                defaultLocation={defaultLoadingLocation?.value}
                defaultCity={data.loadingCity}
                validate={required()}
                readOnly={isDisabledForm}
                label={translate("resources.Order.fields.loadingStreet")}
              />
              <TextInput
                source="loadingNote"
                size="small"
                readOnly={isDisabledForm}
                fullWidth
              />
            </Paper>
          </Grid>
          <Grid item xs={6} direction="column">
            <SelectInput
              size="small"
              source="orderType"
              defaultValue={defaultOrderType?.value}
              optionText="name"
              readOnly={isDisabledForm}
              choices={
                identity?.selectedCompany?.ingresId
                  ? [
                      {
                        id: "ZS",
                        name: translate("resources.Order.orderType.ZS"),
                      },
                      {
                        id: "CZ",
                        name: translate("resources.Order.orderType.CZ"),
                      },
                      {
                        id: "MP",
                        name: "Mražené",
                      },
                    ]
                  : [
                      {
                        id: "NCT",
                        name: translate("resources.Order.orderType.ZS"),
                      },
                      {
                        id: "6",
                        name: translate("resources.Order.orderType.CZ"),
                      },
                      {
                        id: "012",
                        name: translate("resources.Order.orderType.MP"),
                      },
                    ]
              }
              validate={[required()]}
              fullWidth
            />

            {defaultEnabledMultipleActions?.value === "TRUE" ? (
              <SelectInput
                size="small"
                source="action"
                optionText="name"
                isLoading={loadingActions}
                defaultValue={defaultAction?.value}
                readOnly={isDisabledForm}
                choices={
                  actions?.fetchActions?.map((item) => ({
                    id: item.code,
                    name: `${item.code} (${item.name})`,
                  })) || []
                }
                validate={[required()]}
                fullWidth
              />
            ) : null}

            <Paper style={{ padding: 12, paddingTop: 6, paddingBottom: 6 }}>
              <MultipleUnloading {...props} />
            </Paper>
          </Grid>
        </Grid>

        <br />

        <h4>{translate("resources.Order.fields.information")}:</h4>
        <Row>
          <Col style={{ flex: 1 }}>
            <NumberInput
              readOnly={isDisabledForm}
              source="numberOfItems"
              size="small"
              fullWidth
              validate={[required(), minValue(0)]}
            />
          </Col>
          <Col style={{ flex: 1 }}>
            <NumberInput
              source="mass"
              readOnly={isDisabledForm}
              size="small"
              fullWidth
              validate={[required(), minValue(0)]}
            />
          </Col>
        </Row>

        {identity.isAdmin ? (
          <Row>
            <Col style={{ flex: 1 }}>
              <NumberInput
                source="orderCost"
                size="small"
                fullWidth
                validate={[minValue(0)]}
              />
            </Col>
            <Col style={{ flex: 1 }}>
              <SelectInput
                source="orderCurrency"
                defaultValue={defaultCurrency?.value}
                size="small"
                fullWidth
                choices={[
                  {
                    id: "CZK",
                    name: "CZK",
                  },
                  {
                    id: "EUR",
                    name: "EUR",
                  },
                ]}
              />
            </Col>
          </Row>
        ) : null}

        <TextInput
          source="noteForDispatch"
          size="small"
          multiline
          minRows={2}
          readOnly={isDisabledForm}
          fullWidth
        />
        <TextInput
          source="noteForInvoice"
          size="small"
          multiline
          readOnly={isDisabledForm}
          minRows={2}
          validate={[minLength(1), maxLength(32)]}
          fullWidth
        />
        <TextInput
          source="noteOnInvoice"
          size="small"
          multiline
          minRows={2}
          readOnly={isDisabledForm}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} direction="column">
        <h4 style={{ marginBottom: 8 }}>
          {translate("resources.Order.fields.deliveryNotes_ids")}:
        </h4>
        <ArrayInput source="deliveryNotes" label="">
          <SimpleFormIterator
            disableReordering
            disableAdd={isDisabledForm}
            disableClear={isDisabledForm}
            disableRemove
            fullWidth
            inline
            addButton={<AddDeliveryNoteButton />}
          >
            <TextInput
              readOnly={
                ((![
                  OrderStatus.InProgress,
                  OrderStatus.Confirmed,
                  undefined,
                ].includes(data?.status) ||
                  isDisabledDeliveryNotes) &&
                  !identity.isAdmin) ||
                identity?.selectedCompany?.isBlocked
              }
              size="small"
              required
              source="deliveryNoteNumber"
              validate={[minLength(1), maxLength(19), validateDeliveryNotes()]}
              style={{ marginRight: 8, width: "70%" }}
            />
            <FormDataConsumer>
              {({ getSource }) => {
                const watchNumberOfRequiredSscc = watch(
                  getSource("numberOfRequiredSscc"),
                );
                useEffect(() => {
                  if (AUTO_COPY_SSCC_TO_LOADING_UNITS?.value) {
                    setValue(
                      `${getSource("loadingUnits")}.0.amount`,
                      Number(watchNumberOfRequiredSscc),
                    );
                    if (DEFAULT_LOADING_UNIT?.value) {
                      setValue(
                        `${getSource("loadingUnits")}.0.typeId`,
                        DEFAULT_LOADING_UNIT?.value,
                      );
                    }
                  }
                }, [watchNumberOfRequiredSscc]);

                return (
                  <NumberInput
                    source={getSource("numberOfRequiredSscc")}
                    size="small"
                    readOnly={
                      ((![
                        OrderStatus.InProgress,
                        OrderStatus.Confirmed,
                        undefined,
                      ].includes(data?.status) ||
                        isDisabledDeliveryNotes) &&
                        !identity.isAdmin) ||
                      identity?.selectedCompany?.isBlocked
                    }
                    style={{ width: "26%", marginRight: 0 }}
                    defaultValue={0}
                    min={0}
                    validate={[required(), minValue(0)]}
                  />
                );
              }}
            </FormDataConsumer>
            <NumberInput
              source="cash"
              style={{ marginRight: 8, width: "70%" }}
              size="small"
              readOnly={
                ((![
                  OrderStatus.InProgress,
                  OrderStatus.Confirmed,
                  undefined,
                ].includes(data?.status) ||
                  isDisabledDeliveryNotes) &&
                  !identity.isAdmin) ||
                identity?.selectedCompany?.isBlocked
              }
              defaultValue={0}
              validate={[minValue(0)]}
            />
            <SelectInput
              source="currency"
              defaultValue={defaultCurrency?.value}
              readOnly={
                ((![
                  OrderStatus.InProgress,
                  OrderStatus.Confirmed,
                  undefined,
                ].includes(data?.status) ||
                  isDisabledDeliveryNotes) &&
                  !identity.isAdmin) ||
                identity?.selectedCompany?.isBlocked
              }
              size="small"
              style={{ width: "26%", marginRight: 0 }}
              choices={[
                {
                  id: "CZK",
                  name: "CZK",
                },
                {
                  id: "EUR",
                  name: "EUR",
                },
              ]}
            />
            {identity?.selectedCompany?.ingresId ? (
              <TextInput
                source="note"
                size="small"
                readOnly={
                  ((![
                    OrderStatus.InProgress,
                    OrderStatus.Confirmed,
                    undefined,
                  ].includes(data?.status) ||
                    isDisabledDeliveryNotes) &&
                    !identity.isAdmin) ||
                  identity?.selectedCompany?.isBlocked
                }
                fullWidth
              />
            ) : null}
            <FormDataConsumer>
              {({ formData, getSource }) => {
                const { getValues } = useFormContext();
                const currentValue = getValues("locationId");
                const currentProfitCenter = getValues("profitCenter");

                const defaultProfitCenter =
                  identity?.userDefaultValues?.find(
                    (item) => item.property === "PROFIT_CENTER",
                  ) ||
                  identity?.selectedCompany?.companyDefaultValues?.find(
                    (item) => item.property === "PROFIT_CENTER",
                  );

                let allowedIds = Array.isArray(formData?.orderTrips)
                  ? formData.orderTrips.map((t) => t.locationId).filter(Boolean)
                  : [];

                if (currentValue && !allowedIds.includes(currentValue)) {
                  allowedIds = [...allowedIds, currentValue];
                }

                return (
                  <ReferenceInput
                    key={allowedIds.join(",")} // nutné kvůli re-renderu
                    source={getSource("locationId")}
                    reference="Location"
                    filter={{
                      id: { in: allowedIds },
                      integrationType: {
                        equals: record?.integrationType,
                      },
                      profitCenter:
                        currentProfitCenter ||
                        defaultProfitCenter.value ||
                        undefined,
                    }}
                    fullWidth
                  >
                    <AutocompleteInput
                      fullWidth
                      size="small"
                      readOnly={
                        ![
                          OrderStatus.InProgress,
                          OrderStatus.Confirmed,
                          undefined,
                        ].includes(data?.status) || isDisabledDeliveryNotes
                      }
                      optionValue="id"
                      optionText={(row) =>
                        `${row.name}, ${row.street}, ${row.town} (${row.id})`
                      }
                      validate={[required()]}
                    />
                  </ReferenceInput>
                );
              }}
            </FormDataConsumer>

            <ArrayInput
              source="loadingUnits"
              onChange={handleOnChangeDeliveryNotes}
              className="loading-units"
              label={translate(
                "resources.Order.fields.deliveryNotes.loadingUnits.title",
              )}
              readOnly={
                ((![
                  OrderStatus.InProgress,
                  OrderStatus.Confirmed,
                  undefined,
                ].includes(data?.status) ||
                  isDisabledDeliveryNotes) &&
                  !identity.isAdmin) ||
                identity?.selectedCompany?.isBlocked
              }
              defaultValue={
                DEFAULT_LOADING_UNIT?.value
                  ? [
                      {
                        typeId: DEFAULT_LOADING_UNIT?.value,
                        amount: 0,
                      },
                    ]
                  : []
              }
              validate={(loadingUnits) => {
                if (
                  !loadingUnits.length &&
                  defaultEnabledDeliveryNoteLoadingUnitsValidation?.value ===
                    "TRUE" /*||
                  (!loadingUnits.length &&
                    identity?.selectedCompany?.carloPartnerId)*/
                ) {
                  return translate(
                    "resources.Order.errors.missingLoadingUnits",
                  );
                }
                return undefined;
              }}
            >
              <SimpleFormIterator
                disableReordering
                fullWidth
                disableClear
                inline
                removeButton={
                  <MuiButton
                    variant="contained"
                    disabled={isDisabledForm}
                    color="error"
                    size="small"
                    startIcon={<RemoveIcon />}
                    style={{ marginTop: 6 }}
                  >
                    {translate("resources.Global.delete")}
                  </MuiButton>
                }
                addButton={
                  <MuiButton
                    variant="contained"
                    size="small"
                    disabled={isDisabledForm}
                    startIcon={<AddIcon />}
                    style={{ marginTop: 6 }}
                  >
                    {translate("resources.Global.addLoadingUnit")}
                  </MuiButton>
                }
              >
                <FormDataConsumer>
                  {({ scopedFormData, getSource }) => {
                    return (
                      <ReferenceInput
                        readOnly={isDisabledForm || scopedFormData.id}
                        source={getSource("typeId")}
                        reference="LoadingUnit"
                      >
                        <SelectInput
                          readOnly={isDisabledForm}
                          size="small"
                          optionText="name"
                          style={{ width: 300 }}
                        />
                      </ReferenceInput>
                    );
                  }}
                </FormDataConsumer>
                <NumberInput
                  readOnly={isDisabledForm}
                  source="amount"
                  size="small"
                  min={0}
                  validate={[minValue(0)]}
                />
              </SimpleFormIterator>
            </ArrayInput>
            {/* {defaultEnabledDeliveryNoteHalfPallets?.value === "TRUE" ? (
              <NumberInput
                source="numberOfHalfPallets"
                style={{ width: "70%" }}
                size="small"
                readOnly={
                  ((![
                    OrderStatus.InProgress,
                    OrderStatus.Confirmed,
                    undefined,
                  ].includes(data?.status) ||
                    isDisabledDeliveryNotes) &&
                    !identity.isAdmin) ||
                  identity?.selectedCompany?.isBlocked
                }
                min={0}
                validate={[minValue(0)]}
              />
            ) : null}
            {defaultEnabledDeliveryNotePackages?.value === "TRUE" ? (
              <NumberInput
                source="numberOfPackages"
                style={{ width: "70%" }}
                size="small"
                readOnly={
                  ((![
                    OrderStatus.InProgress,
                    OrderStatus.Confirmed,
                    undefined,
                  ].includes(data?.status) ||
                    isDisabledDeliveryNotes) &&
                    !identity.isAdmin) ||
                  identity?.selectedCompany?.isBlocked
                }
                min={0}
                validate={[minValue(0)]}
                label={translate('resources.Order.fields.numberOfPackages_boxes')}
              />
            ) : null}
            {ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES?.value === "TRUE" ? (
              <NumberInput
                source="numberOfPackages"
                style={{ width: "70%" }}
                size="small"
                readOnly={
                  ((![
                    OrderStatus.InProgress,
                    OrderStatus.Confirmed,
                    undefined,
                  ].includes(data?.status) ||
                    isDisabledDeliveryNotes) &&
                    !identity.isAdmin) ||
                  identity?.selectedCompany?.isBlocked
                }
                min={0}
                validate={[minValue(0)]}
                label={translate('resources.Order.fields.numberOfPackages_pallets')}
              />
            ) : null} */}
            <ArrayInput source="sscc" label="SSCC" className="sscc-class">
              <SimpleFormIterator
                disableReordering
                disableAdd
                disableClear
                disableRemove
                inline
              >
                <TextInput readOnly size="small" source="sscc" label="SSCC" />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
    </Grid>
  );
};

const customTransform = (data: any) => {
  console.log("data", data);
  data.deliveryNotes = data.deliveryNotes.map((deliveryNote) => ({
    ...deliveryNote,
    deliveryNoteNumber: deliveryNote.deliveryNoteNumber.trim(),
  }));

  data.orderTrips = data.orderTrips.map((orderTrip) => ({
    ...orderTrip,
    date: dayjs(orderTrip.date).toISOString(),
  }));

  return data;
};

export const OrderCreate = () => {
  const identity = JSON.parse(localStorage.getItem("user"));

  const defaultCurrency = identity?.selectedCompany?.companyDefaultValues?.find(
    (item) => item.property === "CURRENCY",
  );
  const defaultLoadingDate =
    identity?.userDefaultValues?.find(
      (item) => item.property === "LOADING_DATE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "LOADING_DATE",
    );
  const defaultLoadingCity =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_CITY",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_CITY",
    );
  const defaultUnloadingDate =
    identity?.userDefaultValues?.find(
      (item) => item.property === "UNLOADING_DATE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "UNLOADING_DATE",
    );
  const defaultUnloadingLocation =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_LOCATION_ID",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_LOCATION_ID",
    );
  const defaultUnloadingCity =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_CITY",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_UNLOADING_CITY",
    );
  const defaultOrderType =
    identity?.userDefaultValues?.find(
      (item) => item.property === "ORDER_TYPE",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ORDER_TYPE",
    );
  const DEFAULT_LOADING_UNIT =
    identity?.userDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_UNIT",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "DEFAULT_LOADING_UNIT",
    );
  const ENABLED_PREFIX_REFERENCE_NUMBER =
    identity?.userDefaultValues?.find(
      (item) => item.property === "ENABLED_PREFIX_REFERENCE_NUMBER",
    ) ||
    identity?.selectedCompany?.companyDefaultValues?.find(
      (item) => item.property === "ENABLED_PREFIX_REFERENCE_NUMBER",
    );

  const deliveryNotes: Partial<DeliveryNote & any>[] = [
    {
      deliveryNoteNumber: "",
    },
  ];

  if (DEFAULT_LOADING_UNIT?.value) {
    if (!deliveryNotes[0].loadingUnits) {
      deliveryNotes[0].loadingUnits = [];
    }
    deliveryNotes[0].loadingUnits.push({
      typeId: DEFAULT_LOADING_UNIT?.value,
    });
  }

  return (
    <Create redirect="list" transform={customTransform}>
      <SimpleForm
        toolbar={<CustomFormToolbar />}
        style={{ width: 1000 }}
        defaultValues={{
          deliveryNotes,
          referenceOrderNumber: ENABLED_PREFIX_REFERENCE_NUMBER?.value
            ? dayjs().format("YYYY_MM_DD_HH_mm_")
            : "",
          orderType: defaultOrderType?.value || null,
          company: identity?.selectedCompany?.id,
          currency: defaultCurrency?.value || null,
          loadingCity: defaultLoadingCity?.value || null,
          // loadingLocationId: defaultLoadingLocation?.value || null,
          orderTrips: [
            {
              date: defaultUnloadingDate?.value
                ? dayjs()
                    .businessDaysAdd(defaultUnloadingDate?.value)
                    .startOf("day")
                    .toDate()
                : dayjs().startOf("day").toDate(),
              city: defaultUnloadingCity?.value || null,
              tripType: OrderTripType.Unloading,
              orderNumber: 0,
              locationId: defaultUnloadingLocation?.value || null,
            },
          ],
          loadingDate: defaultLoadingDate?.value
            ? dayjs()
                .businessDaysAdd(defaultLoadingDate.value)
                .startOf("day")
                .toDate()
            : dayjs().startOf("day").toDate(),
          unloadingDate: defaultUnloadingDate?.value
            ? dayjs()
                .businessDaysAdd(defaultUnloadingDate?.value)
                .startOf("day")
                .toDate()
            : dayjs().startOf("day").toDate(),
          unloadingCity: defaultUnloadingCity?.value || null,
          unloadingLocationId: defaultUnloadingLocation?.value || null,
        }}
      >
        <Form />
      </SimpleForm>
    </Create>
  );
};

const LoadingPage = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.8);
`;

const CustomFormToolbar = (props) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const translate = useTranslate();
  const identity = JSON.parse(localStorage.getItem("user"));
  const saveButtonRef = useRef();
  const record = useRecordContext();
  const { setValue, trigger } = useFormContext();
  const { saving } = record?.id ? useSaveContext() : { saving: false };
  const [loadingPage, setLoadingPage] = useState(false);

  const [confirmOrder, { loading }] = useMutation(CONFIRM_ORDER_MUTATION);
  const [updateOrder] = useUpdate("Order");

  useEffect(() => {
    setLoadingPage(saving);
  }, [saving]);

  const handleConfirm = async (id: string) => {
    setLoadingPage(true);

    await updateOrder("Order", {
      id,
      data: {
        status: OrderStatus.Confirmed,
      },
    });
    await confirmOrder({
      variables: {
        id,
      },
    });

    setLoadingPage(false);

    notify(translate("resources.Order.fields.success_sent"));
    redirect("/Order");
  };

  return (
    <Toolbar>
      {loadingPage ? (
        <LoadingPage>
          <CircularProgress style={{ width: 50, height: 50 }} />
        </LoadingPage>
      ) : null}
      <SaveButton
        alwaysEnable={
          !loading &&
          !record?.confimedAt &&
          [OrderStatus.InProgress, OrderStatus.Opened, undefined].includes(
            record?.status,
          ) &&
          !identity?.selectedCompany?.isBlocked
        }
        ref={saveButtonRef}
        mutationOptions={{
          onSuccess: () => {
            setLoadingPage(false);
            redirect("/Order");
          },
        }}
      />
      <SaveButton
        label={translate("resources.Order.fields.confirm_and_save")}
        alwaysEnable={
          !loading &&
          !record?.confimedAt &&
          [OrderStatus.InProgress, OrderStatus.Opened, undefined].includes(
            record?.status,
          ) &&
          !identity?.selectedCompany?.isBlocked
        }
        beforeSave={async (values) => {
          const isValid = await trigger();
          if (!isValid) {
            return false;
          }

          if (values.deliveryNotes.length === 0) {
            return confirm(translate("resources.Order.errors.requiredFields"));
          }
          return true;
        }}
        mutationOptions={{
          onSuccess: (data) => {
            setLoadingPage(true);
            handleConfirm(record?.id || data.id);
          },
        }}
        startIcon={
          loading ? (
            <CircularProgress style={{ width: 25, height: 25 }} />
          ) : null
        }
        disabled={
          loading || record?.confimedAt
            ? true
            : false ||
              (![
                OrderStatus.InProgress,
                OrderStatus.Opened,
                undefined,
              ].includes(record?.status) &&
                !window.location.hash.includes("create?source="))
        }
        style={{ marginLeft: 8, padding: "5px 22px" }}
        size="large"
        type="button"
      />
      {!record?.confimedAt ? null : <DownloadDeliveryNoteZip />}
      {!record?.id ? null : (
        <DeleteWithConfirmButton style={{ marginLeft: "auto" }} />
      )}
    </Toolbar>
  );
};

export const OrderEdit = () => {
  return (
    <Edit mutationMode="pessimistic">
      <SimpleForm style={{ width: 1000 }} toolbar={<CustomFormToolbar />}>
        <Form />
      </SimpleForm>
    </Edit>
  );
};

const Col = styled.div`
  flex: 1;
  width: 100%;
`;

export const ScrollableElement = createGlobalStyle`
  html {
    overflow: inherit;
  }
  .MuiStack-root {
    width: 100% !important;
  }
  .RaSimpleFormIterator-line {
    padding: 12px;
    margin-bottom: 12px;
    border-radius: 4px;
    transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    background-color: #fff;
    color: rgba(0, 0, 0, 0.87);
    box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  }
  .RaSimpleFormIterator-line {
    border-bottom: none !important;
  }
  .loading-units {
    padding-top: 8px;
    .RaSimpleFormIterator-line {
      box-shadow: none;
      margin-bottom: 0px;
      padding: 0;
    }
  }
  .sscc-class {
   .RaSimpleFormIterator-list {
    display: flex;
   }
  .RaSimpleFormIterator-line {
    box-shadow: none;
    padding-left: 0;
    margin-bottom: 4px;

    .MuiFormHelperText-root {
      display: none;
    }
   }
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  ${Col} + ${Col} {
    margin-left: 8px;
  }
`;
